import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Grid3X3, RefreshCw, TrendingUp, Clock, CheckCircle2, Building2, FileText, Sheet } from 'lucide-react';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
import { downloadReport } from './OrgReports';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const trDate = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('.') : '-');
const iso = (d) => d.toISOString().slice(0, 10);

const thisWeek = () => {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return { from: iso(monday), to: iso(new Date(monday.getTime() + 6 * 864e5)) };
};

const OccupancyTab = ({ salons = [] }) => {
  const [range, setRange] = useState(thisWeek());
  const [salonId, setSalonId] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSalon, setOpenSalon] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/occupancy/report`, {
        params: { date_from: range.from, date_to: range.to, salon_id: salonId },
      });
      setData(res.data);
      setOpenSalon((p) => p || res.data.salons?.[0]?.id || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dolum raporu yüklenemedi');
    } finally { setLoading(false); }
  }, [range.from, range.to, salonId]);

  useEffect(() => { load(); }, [load]);

  const exportReport = (format) => downloadReport(
    `${API}/occupancy/report/${format}`,
    { date_from: range.from, date_to: range.to, salon_id: salonId },
    `saha-dolum-${range.from}_${range.to}.${format === 'pdf' ? 'pdf' : 'xlsx'}`
  );

  const shiftWeek = (delta) => {    const from = new Date(`${range.from}T00:00:00`);
    const to = new Date(`${range.to}T00:00:00`);
    const span = Math.round((to - from) / 864e5) + 1;
    from.setDate(from.getDate() + delta * span);
    to.setDate(to.getDate() + delta * span);
    setRange({ from: iso(from), to: iso(to) });
  };

  const s = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="occupancy-tab">
      <PageHeader
        icon={Grid3X3}
        title="Saha Dolum Raporlaması"
        description={data ? `${trDate(data.date_from)} – ${trDate(data.date_to)} · saat saat dolu/boş durumu` : 'Yükleniyor...'}
        testid="occupancy-page-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9" onClick={() => exportReport('pdf')}
              data-testid="occupancy-export-pdf">
              <FileText className="w-4 h-4 mr-1.5 text-rose-600" /> PDF
            </Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => exportReport('excel')}
              data-testid="occupancy-export-excel">
              <Sheet className="w-4 h-4 mr-1.5 text-emerald-600" /> Excel
            </Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => setRange(thisWeek())}
              data-testid="occupancy-this-week">Bu Hafta</Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => shiftWeek(-1)}
              data-testid="occupancy-prev">Önceki</Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => shiftWeek(1)}
              data-testid="occupancy-next">Sonraki</Button>
            <Button size="sm" className="h-9 bg-[#0B172A] hover:bg-[#13233C]" onClick={load}
              data-testid="occupancy-refresh">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <Label className="text-[11px] text-slate-500">Başlangıç</Label>
            <Input type="date" className="h-9" value={range.from}
              onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="occupancy-date-from" />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Bitiş</Label>
            <Input type="date" className="h-9" value={range.to}
              onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="occupancy-date-to" />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Salon</Label>
            <Select value={salonId} onValueChange={setSalonId}>
              <SelectTrigger className="h-9" data-testid="occupancy-salon-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Salonlar</SelectItem>
                {salons.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="occupancy-kpis">
        <StatCard icon={TrendingUp} tone="indigo" testid="occ-kpi-rate" label="Genel Doluluk"
          value={`%${s.occupancy ?? 0}`} sub={`${s.salon_count || 0} salon · ${trDate(data?.date_from)}–${trDate(data?.date_to)}`} />
        <StatCard icon={Clock} tone="sky" testid="occ-kpi-open" label="Açık Saat"
          value={`${s.open_hours || 0} sa`} sub="çalışma saatlerine göre" />
        <StatCard icon={CheckCircle2} tone="emerald" testid="occ-kpi-busy" label="Dolu Saat"
          value={`${s.busy_hours || 0} sa`} sub={`${s.free_hours || 0} saat boş`} />
        <StatCard icon={Building2} tone="amber" testid="occ-kpi-top" label="En Yoğun Salon"
          value={`%${s.busiest_salon_occupancy ?? 0}`} sub={s.busiest_salon || '-'} />
      </div>

      {data?.busiest_hours?.length > 0 && (
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="occupancy-busiest-hours">
          <p className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide mb-2">En Yoğun Saatler</p>
          <div className="flex flex-wrap gap-2">
            {data.busiest_hours.map((h) => (
              <div key={h.hour} className="rounded-lg border border-[#E6EDF3] px-3 py-2 min-w-[96px]"
                data-testid={`busy-hour-${h.hour}`}>
                <p className="text-[13px] font-bold text-slate-900 tabular-nums">{h.hour}</p>
                <p className="text-[11px] text-slate-500">%{h.occupancy} · {h.busy}/{h.open}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {(data?.salons || []).map((salon) => {
          const expanded = openSalon === salon.id;
          const tone = salon.occupancy >= 50 ? 'bg-emerald-500' : salon.occupancy >= 25 ? 'bg-amber-500' : salon.occupancy > 0 ? 'bg-rose-500' : 'bg-slate-300';
          return (
            <div key={salon.id} className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden"
              data-testid={`occupancy-salon-${salon.id}`}>
              <button type="button" onClick={() => setOpenSalon(expanded ? null : salon.id)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F8FAFC] transition-colors text-left"
                data-testid={`occupancy-salon-toggle-${salon.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[#0B172A] text-white flex items-center justify-center font-bold shrink-0">
                  {salon.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 text-[14px] truncate">{salon.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {salon.busy_hours} saat dolu · {salon.free_hours} saat boş · {salon.open_hours} saat açık
                  </p>
                </div>
                <div className="w-32 hidden sm:block">
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${tone}`} style={{ width: `${salon.occupancy}%` }} />
                  </div>
                </div>
                <p className="text-lg font-bold tabular-nums text-slate-900 w-16 text-right"
                  data-testid={`occupancy-rate-${salon.id}`}>%{salon.occupancy}</p>
              </button>

              {expanded && (
                <div className="border-t border-[#F1F5F9] p-3 space-y-2 overflow-x-auto">
                  {salon.days.map((day) => (
                    <div key={day.date} className="flex items-start gap-3" data-testid={`occupancy-day-${salon.id}-${day.date}`}>
                      <div className="w-28 shrink-0 pt-1">
                        <p className="text-[12px] font-semibold text-slate-700">{day.weekday}</p>
                        <p className="text-[10px] text-slate-400">{trDate(day.date)}</p>
                      </div>
                      {day.closed ? (
                        <span className="text-[11px] text-rose-600 bg-rose-50 rounded-md px-2 py-1">Kapalı</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {day.slots.map((sl) => (
                            <div key={sl.hour}
                              title={sl.busy ? `${sl.hour}-${sl.end} · ${sl.customer_name}` : `${sl.hour}-${sl.end} · Boş`}
                              data-testid={`slot-${salon.id}-${day.date}-${sl.hour}`}
                              className={`rounded-md px-2 py-1 text-[10px] font-medium border min-w-[58px] text-center ${sl.busy
                                ? 'bg-[#176B91] text-white border-[#176B91]'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-100'}`}>
                              <span className="tabular-nums">{sl.hour}</span>
                              <span className="block text-[9px] truncate max-w-[72px]">
                                {sl.busy ? (sl.customer_name || 'Dolu') : 'Boş'}
                              </span>
                            </div>
                          ))}
                          <span className="text-[11px] text-slate-500 self-center ml-1">%{day.occupancy}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {!loading && (data?.salons || []).length === 0 && (
          <div className="text-center py-12 text-slate-500">Bu aralıkta gösterilecek salon bulunamadı</div>
        )}
      </div>
    </div>
  );
};

export default OccupancyTab;
